import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import Header from "@/components/header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ThumbsUp, ThumbsDown, Star, Menu } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { setMetaTags } from "@/hooks/use-meta-tags";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import PuzzleChat from "@/components/PuzzleChat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { PuzzleDetailsTab } from "@/components/puzzles/PuzzleDetailsTab";
import { PuzzleHintsTab } from "@/components/puzzles/PuzzleHintsTab";
import { PuzzleReviewsTab } from "@/components/puzzles/PuzzleReviewsTab";
import { PuzzleAnswersTab } from "@/components/puzzles/PuzzleAnswersTab";
import { RatingDialog } from "@/pages/desafios-cacatais/components/RatingDialog";
import { AnswerDialog } from "@/pages/desafios-cacatais/components/AnswerDialog";

interface CustomPuzzle {
  id: number;
  title: string;
  description: string;
  address: string;
  prize: string;
  createdAt: string;
  userId: number;
  solved: boolean;
  imageUrl?: string;
  solvedByUser?: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
  user: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

interface User {
  id: number;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  cacatais: number;
}

interface PuzzleHint {
  id: number;
  content: string;
  price: number;
  customPuzzleId: number;
  createdAt: string;
  updatedAt: string;
  purchased?: boolean;
  likesCount?: number;
  dislikesCount?: number;
  userVote?: number; // 1 for like, -1 for dislike, 0 for no vote
}

interface PuzzleRating {
  id: number;
  customPuzzleId: number;
  userId: number;
  rating: number;
  comment?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

interface PuzzleRatingsResponse {
  ratings: PuzzleRating[];
  averageRating: number;
  totalRatings: number;
}

interface PuzzleAnswer {
  id: number;
  customPuzzleId: number;
  answer: string;
  createdAt: string;
  user: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

export default function CustomPuzzlePage({ params }: { params: { id: string } }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  
  const [privateKey, setPrivateKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privateKeyError, setPrivateKeyError] = useState<string | null>(null);
  const [isSolveDialogOpen, setIsSolveDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Rating dialog state
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
  const [ratingPuzzleId, setRatingPuzzleId] = useState<number | null>(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  
  // Answer dialog state
  const [isAnswerDialogOpen, setIsAnswerDialogOpen] = useState(false);
  const [answerPuzzleId, setAnswerPuzzleId] = useState<number | null>(null);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

  // Add answer mutation
  const addAnswerMutation = useMutation({
    mutationFn: async ({ puzzleId, content }: { puzzleId: number; content: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit answer");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: t("desafios_cacatais.submit_success", { fallback: "Answer submitted successfully" }),
      });
      // Invalidate the answers query to refresh the list
      if (answerPuzzleId) {
        queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${answerPuzzleId}/answers`] });
      }
      closeAnswerDialog();
    },
    onError: (error: Error) => {
      toast({
        title: t("desafios_cacatais.answers.submit_error", { fallback: "Error submitting answer" }),
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmittingAnswer(false);
    }
  });

  const handleAnswerSubmit = (content: string) => {
    if (answerPuzzleId) {
      setIsSubmittingAnswer(true);
      // Use the mutation to submit the answer
      addAnswerMutation.mutate({ puzzleId: answerPuzzleId, content });
    }
  };

  const { data: puzzle, isLoading: isPuzzleLoading } = useQuery<CustomPuzzle>({
    queryKey: [`/api/puzzles/custom/${params.id}`],
  });

  const { data: user } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  const { data: hints, isLoading: isHintsLoading } = useQuery<PuzzleHint[]>({
    queryKey: [`/api/puzzles/custom/${params.id}/hints`],
    enabled: !!puzzle,
  });

  const { data: purchasedHints, isLoading: isPurchasedHintsLoading } = useQuery<PuzzleHint[]>({
    queryKey: [`/api/puzzles/custom/${params.id}/hints/purchased`],
    enabled: !!user && !!puzzle && user.id !== puzzle.userId,
  });
  
  // Query for fetching puzzle ratings
  const { data: ratingsData, isLoading: isRatingsLoading } = useQuery<PuzzleRatingsResponse>({
    queryKey: [`/api/puzzles/custom/${params.id}/ratings`],
    enabled: !!puzzle,
  });
  
  // Query for fetching messages (comments)
  const { data: messages } = useQuery<any[]>({
    queryKey: [`/api/puzzles/custom/${params.id}/messages`],
    enabled: !!puzzle,
  });
  
  // Query for fetching puzzle answers
  const { data: puzzleAnswers, isLoading: isPuzzleAnswersLoading } = useQuery<PuzzleAnswer[]>({
    queryKey: [`/api/puzzles/custom/${params.id}/answers`],
    enabled: !!puzzle && puzzle.solved,
  });
  
  // Function to handle hint voting
  const handleVoteHint = (hintId: number, voteValue: number) => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.hints.auth_required"),
        description: t("desafios_cacatais.hints.auth_message", { 
          fallback: "You need to be logged in to vote on hints." 
        }),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }
    
    voteHintMutation.mutate({ hintId, vote: voteValue });
  };

  // Set title for the puzzle
  useTitle(puzzle?.title || t("desafios_cacatais.loading"));

  const solvePuzzleMutation = useMutation({
    mutationFn: async ({ puzzleId, privateKey }: { puzzleId: number; privateKey: string }) => {
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/solve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ private_key: privateKey }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to solve puzzle");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.success"),
        description: t("desafios_cacatais.solve_dialog.success_description"),
      });
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${params.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.error"),
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const purchaseHintMutation = useMutation({
    mutationFn: async (hintId: number) => {
      const response = await fetch(`/api/puzzles/custom/${params.id}/hints/${hintId}/purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to purchase hint");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({ 
        title: t("desafios_cacatais.hints.purchase_success"),
        description: t("desafios_cacatais.hints.purchase_success_description", { 
          fallback: "Hint purchased successfully" 
        }),
      });
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${params.id}/hints/purchased`] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.hints.purchase_error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Hint voting mutation
  const voteHintMutation = useMutation({
    mutationFn: async ({ hintId, vote }: { hintId: number; vote: number }) => {
      const response = await fetch(`/api/puzzles/custom/${params.id}/hints/${hintId}/vote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ vote }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to vote on hint");
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${params.id}/hints`] });
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${params.id}/hints/purchased`] });
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.hints.vote_error", { fallback: "Voting Error" }),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const submitRatingMutation = useMutation<any, Error, { puzzleId: number; rating: number; comment: string }>({   
    mutationFn: async ({ puzzleId, rating, comment }) => {
      setIsSubmittingRating(true);
      const response = await fetch(`/api/puzzles/custom/${puzzleId}/rate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          rating,
          comment: comment.trim() || null
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to submit rating");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: t("desafios_cacatais.rating_dialog.success", { fallback: "Rating submitted successfully" }),
      });
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${params.id}/ratings`] });
      setIsRatingDialogOpen(false);
      setRatingPuzzleId(null);
    },
    onError: (error: Error) => {
      toast({ 
        title: t("desafios_cacatais.rating_dialog.error", { fallback: "Error submitting rating" }),
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmittingRating(false);
    }
  });

  const handlePurchaseHint = (hintId: number, price: number) => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.hints.auth_required"),
        description: t("desafios_cacatais.hints.auth_message", { 
          fallback: "You need to be logged in to purchase hints." 
        }),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }

    if (user.cacatais < price) {
      toast({ 
        title: t("desafios_cacatais.hints.insufficient_funds"),
        description: t("desafios_cacatais.hints.insufficient_funds_message", { 
          fallback: "You don't have enough caçatais to purchase this hint." 
        }),
        variant: "destructive"
      });
      return;
    }

    purchaseHintMutation.mutate(hintId);
  };

  const handleSolveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puzzle) return;
    
    // Reset any previous error
    setPrivateKeyError(null);
    
    // Validate private key format (64 character hex string)
    const hexRegex = /^[0-9a-fA-F]{64}$/;
    if (!hexRegex.test(privateKey)) {
      setPrivateKeyError(t("desafios_cacatais.solve_dialog.invalid_format", { 
        fallback: "Private key must be a 64-character hexadecimal string." 
      }));
      return;
    }
    
    setIsSubmitting(true);
    solvePuzzleMutation.mutate({ puzzleId: puzzle.id, privateKey });
  };

  const openSolveDialog = () => {
    if (!user) {
      toast({ 
        title: t("desafios_cacatais.solve_dialog.auth_required"),
        description: t("desafios_cacatais.solve_dialog.auth_message"),
        variant: "destructive"
      });
      setLocation("/auth");
      return;
    }
    
    setIsSolveDialogOpen(true);
    setPrivateKey("");
    setPrivateKeyError(null);
  };

  const closeDialog = () => {
    setIsSolveDialogOpen(false);
    setPrivateKey("");
    setPrivateKeyError(null);
  };

  const openRatingDialog = (puzzleId: number) => {
    setRatingPuzzleId(puzzleId);
    setIsRatingDialogOpen(true);
  };

  const closeRatingDialog = () => {
    setRatingPuzzleId(null);
    setIsRatingDialogOpen(false);
  };
  
  // Function to open the answer dialog
  const openAnswerDialog = (puzzleId: number) => {
    setAnswerPuzzleId(puzzleId);
    setIsAnswerDialogOpen(true);
  };
  
  // Function to close the answer dialog
  const closeAnswerDialog = () => {
    setAnswerPuzzleId(null);
    setIsAnswerDialogOpen(false);
  };
  
  // Function to handle rating submission
  const handleRatingSubmit = (rating: number, comment: string) => {
    if (ratingPuzzleId) {
      submitRatingMutation.mutate({ puzzleId: ratingPuzzleId, rating, comment });
    }
  };
  


  // Function to get user's current rating for the puzzle if it exists
  const getUserRatingForCurrentPuzzle = (): number => {
    if (!user || !ratingsData || !puzzle) return 0;
    
    const userRating = ratingsData.ratings.find(r => r.userId === user.id);
    return userRating?.rating || 0;
  };

  // Set meta tags based on loading state
  useEffect(() => {
    if (isPuzzleLoading || !puzzle) {
      // Set default meta tags for loading state
      setMetaTags({
        title: t("desafios_cacatais.loading"),
        description: "Loading Bitcoin puzzle details..."
      });
    } else {
      // Set meta tags for the loaded puzzle
      setMetaTags({
        title: puzzle.title,
        description: puzzle.description,
        imageUrl: puzzle.imageUrl,
        type: "website"
      });
    }
  }, [isPuzzleLoading, puzzle, t]);

  if (isPuzzleLoading || !puzzle) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const processedHints = hints?.map(hint => {
    // Check if this hint has been purchased
    const purchasedHint = purchasedHints?.find(ph => ph.id === hint.id);
    
    if (purchasedHint) {
      // If hint is purchased, use all data from the purchased hint object including content
      return { ...purchasedHint, purchased: true };
    } else {
      // Otherwise just mark as not purchased
      return { ...hint, purchased: false };
    }
  });

  // If the puzzle is solved, we'll show all hint contents to everyone
  const shouldShowAllHints = puzzle.solved;

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="container mx-auto px-6 pt-24 pb-12">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-white break-words overflow-hidden">{puzzle.title}</CardTitle>
                <CardDescription className="text-zinc-400">
                  {t("desafios_cacatais.prize", { amount: parseFloat(puzzle.prize).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                </CardDescription>
              </div>
              {puzzle.solved ? (
                <Badge variant="destructive">{t("puzzles.solved")}</Badge>
              ) : (
                <Badge variant="success">{t("puzzles.notSolved")}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="details" className="space-y-4" onValueChange={setActiveTab} value={activeTab}>
              {/* Mobile hamburger menu */}
              <div className="md:hidden flex items-center justify-between mb-4">
                <div className="text-white font-medium">
                  {activeTab === "details" && t("desafios_cacatais.details_tab", { fallback: "Detalhes" })}
                  {activeTab === "hints" && t("desafios_cacatais.hints_tab", { fallback: "Dicas", count: hints && hints.length > 0 ? `(${hints.length})` : "" })}
                  {activeTab === "comments" && t("desafios_cacatais.comments_tab", { fallback: "Comentários", count: messages && messages.length > 0 ? `(${messages.length})` : "" })}
                  {activeTab === "reviews" && t("desafios_cacatais.reviews_tab", { fallback: "Reviews", count: ratingsData && ratingsData.totalRatings > 0 ? `(${ratingsData.totalRatings})` : "" })}
                  {activeTab === "customPuzzleAnswers" && t("desafios_cacatais.answers_tab", { fallback: "Solutions", count: puzzleAnswers && puzzleAnswers.length > 0 ? `(${puzzleAnswers.length})` : "" })}
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-white"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Mobile dropdown menu */}
              {isMobileMenuOpen && (
                <div className="md:hidden bg-zinc-800 rounded-md mb-4 overflow-hidden">
                  <div className="flex flex-col">
                    <button 
                      className={`text-left px-4 py-3 ${activeTab === "details" ? "bg-zinc-700" : ""}`}
                      onClick={() => {
                        setActiveTab("details");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {t("desafios_cacatais.details_tab", { fallback: "Detalhes" })}
                    </button>
                    <button 
                      className={`text-left px-4 py-3 ${activeTab === "hints" ? "bg-zinc-700" : ""}`}
                      onClick={() => {
                        setActiveTab("hints");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {t("desafios_cacatais.hints_tab", { fallback: "Dicas", count: hints && hints.length > 0 ? `(${hints.length})` : "" })}
                    </button>
                    <button 
                      className={`text-left px-4 py-3 ${activeTab === "comments" ? "bg-zinc-700" : ""}`}
                      onClick={() => {
                        setActiveTab("comments");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {t("desafios_cacatais.comments_tab", { fallback: "Comentários", count: messages && messages.length > 0 ? `(${messages.length})` : "" })}
                    </button>
                    {puzzle.solved && (
                      <>
                        <button 
                          className={`text-left px-4 py-3 ${activeTab === "reviews" ? "bg-zinc-700" : ""}`}
                          onClick={() => {
                            setActiveTab("reviews");
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t("desafios_cacatais.reviews_tab", { fallback: "Reviews", count: ratingsData && ratingsData.totalRatings > 0 ? `(${ratingsData.totalRatings})` : "" })}
                        </button>
                        <button 
                          className={`text-left px-4 py-3 ${activeTab === "customPuzzleAnswers" ? "bg-zinc-700" : ""}`}
                          onClick={() => {
                            setActiveTab("customPuzzleAnswers");
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t("desafios_cacatais.answers_tab", { fallback: "Solutions", count: puzzleAnswers && puzzleAnswers.length > 0 ? `(${puzzleAnswers.length})` : "" })}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Desktop tabs */}
              <TabsList className={`hidden md:grid w-full ${puzzle.solved ? 'grid-cols-5' : 'grid-cols-4'}`}>
                <TabsTrigger value="details">{t("desafios_cacatais.details_tab", { fallback: "Detalhes" })}</TabsTrigger>
                <TabsTrigger value="hints">{t("desafios_cacatais.hints_tab", { fallback: "Dicas", count: hints && hints.length > 0 ? `(${hints.length})` : "" })}</TabsTrigger>
                <TabsTrigger value="comments">{t("desafios_cacatais.comments_tab", { fallback: "Comentários", count: messages && messages.length > 0 ? `(${messages.length})` : "" })}</TabsTrigger>
                <TabsTrigger value="reviews">{t("desafios_cacatais.reviews_tab", { fallback: "Reviews", count: ratingsData && ratingsData.totalRatings > 0 ? `(${ratingsData.totalRatings})` : "" })}</TabsTrigger>
                {puzzle.solved && (
                  <>
                    <TabsTrigger value="customPuzzleAnswers">{t("desafios_cacatais.answers_tab", { fallback: "Solutions", count: puzzleAnswers && puzzleAnswers.length > 0 ? `(${puzzleAnswers.length})` : "" })}</TabsTrigger>
                  </>
                )}
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details">
                <PuzzleDetailsTab 
                  puzzle={puzzle} 
                  user={user} 
                  setIsImageModalOpen={setIsImageModalOpen} 
                  openSolveDialog={openSolveDialog} 
                />
              </TabsContent>

              {/* Hints Tab */}
              <TabsContent value="hints">
                <PuzzleHintsTab
                  puzzle={puzzle}
                  user={user}
                  hints={hints}
                  processedHints={processedHints}
                  shouldShowAllHints={shouldShowAllHints}
                  isHintsLoading={isHintsLoading}
                  isPurchasedHintsLoading={isPurchasedHintsLoading}
                  voteHintMutationIsPending={voteHintMutation.isPending}
                  purchaseHintMutationIsPending={purchaseHintMutation.isPending}
                  handleVoteHint={handleVoteHint}
                  handlePurchaseHint={handlePurchaseHint}
                />
              </TabsContent>

              {/* Comments Tab */}
              <TabsContent value="comments" className="space-y-6">
                <PuzzleChat puzzleId={puzzle.id} currentUser={user} />
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews">
                <PuzzleReviewsTab
                  puzzleId={puzzle.id}
                  ratingsData={ratingsData}
                  isRatingsLoading={isRatingsLoading}
                  onOpenRatingDialog={openRatingDialog}
                />
              </TabsContent>

              {puzzle.solved && (
                <TabsContent value="customPuzzleAnswers">
                  <PuzzleAnswersTab
                    puzzleAnswers={puzzleAnswers}
                    isPuzzleAnswersLoading={isPuzzleAnswersLoading}
                    puzzleId={puzzle.id}
                    onOpenAnswerDialog={openAnswerDialog}
                    currentUser={user}
                    puzzleOwnerId={puzzle.user.id}
                    solvedByUserId={puzzle.solvedByUser?.id}
                  />
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Image Modal */}
      {puzzle.imageUrl && (
        <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl p-1 sm:p-2">
            <div className="relative w-full h-full">
              <img 
                src={puzzle.imageUrl} 
                alt={puzzle.title}
                className="w-full h-full object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Solve Puzzle Dialog */}
      <Dialog open={isSolveDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>{t("desafios_cacatais.solve_dialog.title")}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t("desafios_cacatais.solve_dialog.description")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSolveSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="private-key">{t("desafios_cacatais.solve_dialog.private_key")}</Label>
                <Input
                  id="private-key"
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  placeholder="e.g., 5f4dcc3b5aa765d61d8327deb882cf99..."
                  className="bg-zinc-800 border-zinc-700"
                />
                {privateKeyError && (
                  <p className="text-red-500 text-sm">{privateKeyError}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={closeDialog}
                disabled={isSubmitting}
              >
                {t("desafios_cacatais.solve_dialog.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("desafios_cacatais.solve_dialog.submitting")}
                  </>
                ) : (
                  t("desafios_cacatais.solve_dialog.submit")
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Rating Dialog */}
      <RatingDialog
        isOpen={isRatingDialogOpen}
        onClose={closeRatingDialog}
        onSubmit={handleRatingSubmit}
        isSubmitting={isSubmittingRating}
        initialRating={getUserRatingForCurrentPuzzle()}
      />

      {/* Answer Dialog */}
      <AnswerDialog
        isOpen={answerPuzzleId !== null}
        onClose={closeAnswerDialog}
        onSubmit={handleAnswerSubmit}
        isSubmitting={isSubmittingAnswer}
      />
    </div>
  );
}
